import { useState } from 'react';

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useDeactivatePricingRule, usePricingRules } from '../../hooks';
import { formatPrice } from '../../lib/format';
import { RULE_TYPE_LABELS, RULE_TYPE_OPTIONS } from '../../model/options';
import type { PricingRuleResponse } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { RuleFormDialog } from './RuleFormDialog';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function ruleValue(rule: PricingRuleResponse, t: TFunction): string {
  if (rule.discountPercent != null) return `−${rule.discountPercent}%`;
  if (rule.priceOverride != null) return formatPrice(rule.priceOverride);
  if (rule.taxRate != null) {
    return t('pricingRulesPanel.valueTax', { percent: Math.round(rule.taxRate * 100) });
  }
  return '—';
}

/** Pricing rules — list, create, update (new version) and deactivate. */
export function PricingRulesPanel({ canManage, onResult }: PanelProps) {
  const { t } = useTranslation('catalog');
  const [ruleType, setRuleType] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PricingRuleResponse | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<PricingRuleResponse | null>(null);

  const query = usePricingRules({ ruleType: ruleType || undefined, page, size });
  const deactivate = useDeactivatePricingRule();
  const rules = query.data?.content ?? [];

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={t('pricingRulesPanel.ruleTypeFilterLabel')}
          value={ruleType}
          onChange={(e) => {
            setRuleType(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">{t('pricingRulesPanel.allTypes')}</MenuItem>
          {RULE_TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {t(o.label)}
            </MenuItem>
          ))}
        </TextField>
        <Stack flex={1} />
        {canManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            {t('pricingRulesPanel.addRule')}
          </Button>
        ) : null}
      </Stack>

      {query.isLoading ? (
        <LoadingState label={t('pricingRulesPanel.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('pricingRulesPanel.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      ) : rules.length === 0 ? (
        <EmptyState
          title={t('pricingRulesPanel.empty.title')}
          description={t('pricingRulesPanel.empty.description')}
          icon={<RuleRoundedIcon />}
          action={
            canManage ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                {t('pricingRulesPanel.addRule')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TablePageShell
          footer={
            <CompactTablePagination
              count={query.data?.totalElements ?? 0}
              page={page}
              rowsPerPage={size}
              hideRowsPerPage
              onPageChange={(_e, next) => setPage(next)}
              onRowsPerPageChange={(e) => {
                setSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('common:pagination.of')} ${count}`
              }
            />
          }
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('pricingRulesPanel.table.product')}</TableCell>
                  <TableCell>{t('pricingRulesPanel.table.type')}</TableCell>
                  <TableCell>{t('pricingRulesPanel.table.value')}</TableCell>
                  <TableCell align="right">{t('pricingRulesPanel.table.priority')}</TableCell>
                  <TableCell>{t('pricingRulesPanel.table.status')}</TableCell>
                  <TableCell align="right">{t('pricingRulesPanel.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {rule.productId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {RULE_TYPE_LABELS[rule.ruleType] ? t(RULE_TYPE_LABELS[rule.ruleType]) : rule.ruleType}
                    </TableCell>
                    <TableCell>{ruleValue(rule, t)}</TableCell>
                    <TableCell align="right">{rule.priority}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={rule.active ? 'success' : 'default'}
                        label={
                          rule.active
                            ? t('pricingRulesPanel.statusActive')
                            : t('pricingRulesPanel.statusDisabled')
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip
                          title={
                            canManage
                              ? t('pricingRulesPanel.rowActions.edit')
                              : t('pricingRulesPanel.rowActions.view')
                          }
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t('pricingRulesPanel.rowActions.editAria')}
                            onClick={() => {
                              setEditing(rule);
                              setDialogOpen(true);
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('pricingRulesPanel.rowActions.deactivate')}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!canManage || !rule.active}
                              aria-label={t('pricingRulesPanel.rowActions.deactivateAria')}
                              onClick={() => setDeactivateTarget(rule)}
                            >
                              <BlockRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}

      <RuleFormDialog
        open={dialogOpen}
        rule={editing}
        onClose={() => setDialogOpen(false)}
        onResult={onResult}
      />

      <Dialog open={deactivateTarget !== null} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>{t('pricingRulesPanel.deactivateDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('pricingRulesPanel.deactivateDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)} disabled={deactivate.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deactivate.isPending}
            onClick={() => {
              if (!deactivateTarget) return;
              deactivate.mutate(deactivateTarget.id, {
                onSuccess: () => {
                  setDeactivateTarget(null);
                  onResult('success', t('pricingRulesPanel.deactivateDialog.confirmed'));
                },
                onError: (error) => onResult('error', error.message),
              });
            }}
          >
            {t('pricingRulesPanel.rowActions.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
